import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { api } from '@/lib/api'
import { environment } from '@/config/environment'
import { fetchPlaidItems, type PlaidItemSummary } from './plaid-items'
import { useTheme, type ThemeMode } from './theme'

/**
 * Settings — five panels:
 *   - Profile (email, sign-out)
 *   - Bank connections (Plaid items, re-link CTAs for login-required)
 *   - Appearance (theme toggle: system / light / dark)
 *   - Your data (GDPR export + delete-account)
 *   - About (env, version)
 *
 * The Plaid-items list reads directly from `users/{uid}/plaid_items`
 * (Firestore client). Re-link for ITEM_LOGIN_REQUIRED is a future
 * ticket — for now we just surface the status so the user knows
 * what's wrong.
 */
export function SettingsScreen() {
  const { user, signOut } = useAuth()
  const { mode, setMode } = useTheme()
  const qc = useQueryClient()
  const items = useQuery({
    queryKey: ['plaid_items'] as const,
    queryFn: fetchPlaidItems,
    staleTime: 60_000,
  })

  const exportData = useMutation({
    mutationFn: async () => {
      // Direct fetch (bypassing api.ts) so we can stream the file
      // straight to a download rather than parse it as JSON in memory.
      const token = await user?.getIdToken()
      if (!token) throw new Error('Not signed in')
      const res = await fetch(`${environment.apiBaseURL}/users/me/export`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `crawfish-budget-export-${Date.now()}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
  })

  const deleteAccount = useMutation({
    mutationFn: async () => api.delete('/users/me'),
    onSuccess: async () => {
      qc.clear()
      await signOut()
    },
  })

  const [confirmDelete, setConfirmDelete] = useState('')
  const canDelete = confirmDelete.trim().toLowerCase() === 'delete'

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold">Settings</h1>

      <Panel title="Profile">
        <Row label="Email">{user?.email ?? '—'}</Row>
        <Row label="User ID" mono>
          {user?.uid ?? '—'}
        </Row>
        <div className="pt-2">
          <button
            type="button"
            onClick={() => signOut()}
            className="rounded-md border border-zinc-300 px-3 py-1 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            Sign out
          </button>
        </div>
      </Panel>

      <Panel title="Bank connections">
        {items.isLoading ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : items.isError ? (
          <p role="alert" className="text-sm text-overspent">
            Couldn&apos;t load your bank connections.
          </p>
        ) : (items.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-zinc-500">
            No banks linked yet. Use the <strong>Accounts</strong> tab to add one.
          </p>
        ) : (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {items.data!.map((item) => (
              <BankRow key={item.itemId} item={item} />
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Appearance">
        <Row label="Theme">
          <div className="flex gap-1">
            {(['system', 'light', 'dark'] as ThemeMode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`rounded-md border px-3 py-1 text-sm capitalize ${
                  mode === m
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                    : 'border-zinc-300 hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800'
                }`}
                aria-pressed={mode === m}
              >
                {m}
              </button>
            ))}
          </div>
        </Row>
      </Panel>

      <Panel title="Import">
        <p className="text-sm text-zinc-500">
          Migrate from YNAB or another CSV-based budget tool.
        </p>
        <div className="pt-2">
          <Link
            to="/import"
            className="inline-block rounded-md border border-zinc-300 px-3 py-2 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            Import from YNAB
          </Link>
        </div>
      </Panel>

      <Panel title="Your data">
        <p className="text-sm text-zinc-500">
          Download everything we hold about your account, or delete it
          permanently. Both routes are auditable on the backend.
        </p>
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            onClick={() => exportData.mutate()}
            disabled={exportData.isPending}
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm hover:bg-zinc-100 disabled:opacity-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            {exportData.isPending ? 'Preparing…' : 'Download my data'}
          </button>
        </div>
        {exportData.isError && (
          <p role="alert" className="pt-2 text-sm text-overspent">
            {exportData.error instanceof Error
              ? exportData.error.message
              : 'Export failed'}
          </p>
        )}

        <details className="pt-4">
          <summary className="cursor-pointer text-sm font-medium text-overspent">
            Delete account
          </summary>
          <div className="space-y-2 pt-2">
            <p className="text-sm text-zinc-500">
              This permanently deletes every transaction, budget, account,
              and conversation. There is no undo. Type <code>delete</code>{' '}
              below to confirm.
            </p>
            <input
              type="text"
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              placeholder='type "delete"'
              aria-label="Confirm delete"
              className="w-full max-w-xs rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
            />
            <button
              type="button"
              onClick={() => deleteAccount.mutate()}
              disabled={!canDelete || deleteAccount.isPending}
              className="block rounded-md bg-overspent px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {deleteAccount.isPending ? 'Deleting…' : 'Permanently delete account'}
            </button>
            {deleteAccount.isError && (
              <p role="alert" className="text-sm text-overspent">
                {deleteAccount.error instanceof Error
                  ? deleteAccount.error.message
                  : 'Delete failed'}
              </p>
            )}
          </div>
        </details>
      </Panel>

      <Panel title="About">
        <Row label="Environment">{import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE}</Row>
        <Row label="App version">0.1.0 (alpha)</Row>
        <Row label="Source">
          <a
            href="https://gitlab.com/samuelbirk-private/crawfish/budget"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline dark:text-blue-400"
          >
            samuelbirk-private/crawfish/budget
          </a>
        </Row>
        <Row label="Privacy">
          <a
            href="https://site.crawfishlabs.ai/budget/privacy"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline dark:text-blue-400"
          >
            Privacy policy
          </a>
        </Row>
      </Panel>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      aria-labelledby={`panel-${title}`}
      className="space-y-3 rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900"
    >
      <h2 id={`panel-${title}`} className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  )
}

function Row({
  label,
  children,
  mono,
}: {
  label: string
  children: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className={mono ? 'font-mono text-xs text-zinc-700 dark:text-zinc-300' : ''}>
        {children}
      </span>
    </div>
  )
}

function BankRow({ item }: { item: PlaidItemSummary }) {
  const needsRelink = item.status === 'login_required'
  const lastSynced =
    item.lastSyncedAt && Number.isFinite(item.lastSyncedAt)
      ? new Date(item.lastSyncedAt).toLocaleString()
      : 'never'
  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm">
      <div className="flex items-center gap-3">
        {item.institutionLogo ? (
          <img
            src={`data:image/png;base64,${item.institutionLogo}`}
            alt=""
            aria-hidden
            className="h-8 w-8 rounded"
          />
        ) : (
          <div
            aria-hidden
            className="flex h-8 w-8 items-center justify-center rounded bg-zinc-100 text-xs text-zinc-500 dark:bg-zinc-800"
          >
            🏦
          </div>
        )}
        <div>
          <div className="font-medium">{item.institutionName}</div>
          <div className="text-xs text-zinc-500">Last synced: {lastSynced}</div>
        </div>
      </div>
      <div>
        {needsRelink ? (
          <span className="rounded-full bg-overspent-subtle px-2 py-0.5 text-xs font-medium text-overspent">
            Re-link required
          </span>
        ) : (
          <span className="rounded-full bg-funded-subtle px-2 py-0.5 text-xs font-medium text-funded">
            Active
          </span>
        )}
      </div>
    </li>
  )
}
